import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AiThread, AiMessage } from '@lognai/plugin-ai-core';
import { ChatMessage } from './ChatMessage';
import { ChatComposer } from './ChatComposer';
import { ThreadList } from './ThreadList';
import { useChatStream, type DraftRef } from '../../hooks/use-chat-stream';
import { useAiThreads, useCreateAiThread, useUpdateAiThread } from '../../hooks/use-threads';
import { useThreadMessages } from '../../hooks/use-thread-messages';
import { useAiBundlesForThread } from '../../hooks/use-bundle';
import { useQueryClient } from '@tanstack/react-query';
import { aiQueryKeys } from '../../hooks/query-keys';
import { DraftBundleCard } from '../DraftBundleCard';
import { BundleReviewPanel } from '../BundleReviewPanel';
import { UndoBanner } from '../UndoBanner';
import { KillSwitchBanner } from '../KillSwitchBanner';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import { useEntityLabel } from '../../hooks/use-entity-label';

export interface ChatSidebarProps {
  open: boolean;
  onClose: () => void;
  pageContext?: {
    pluginId: string;
    entityType: string;
    entityId: string;
    url: string;
  };
  /** If supplied, the sidebar auto-submits this as the first user message.
   *  Used by the Command Palette's AI fallback to carry the typed query
   *  into the conversation. */
  initialPrompt?: string;
  /** If supplied, the sidebar opens directly into this thread. */
  initialThreadId?: string;
}

interface LocalMessage {
  role: 'user' | 'assistant';
  content: string;
  toolTrace?: Array<{ name: string; uiLabel?: string; done: boolean }>;
  draftRefs?: DraftRef[];
  messageId?: string;
}

// Convert a persisted AiMessage from the API into the sidebar's LocalMessage
// shape. Tool calls become a "done" trace; tool/system rows are dropped from
// the rendered transcript (they're context for the model, not user-facing).
const toLocalMessage = (m: AiMessage): LocalMessage | null => {
  if (m.role !== 'user' && m.role !== 'assistant') return null;
  return {
    role: m.role,
    content: m.content,
    toolTrace: (m.toolCalls ?? []).map((tc) => ({
      name: tc.name,
      uiLabel: undefined,
      done: true,
    })),
    draftRefs: (m.draftRefs ?? []) as DraftRef[],
    messageId: m.id,
  };
};

const humanize = (raw: string): string => {
  if (!raw) return raw;
  const spaced = raw.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced[0]!.toUpperCase() + spaced.slice(1);
};

// Derive a short, human-readable title from the user's first message.
const deriveTitle = (text: string): string => {
  const first = text.trim().split('\n')[0]!.trim();
  if (first.length <= 60) return first;
  const truncated = first.slice(0, 60).replace(/\s\S*$/, '');
  return truncated || first.slice(0, 60);
};

// Pre-token placeholder: shows what the agent is actually doing right now.
// The label is derived from the in-flight tool (or "Thinking" if none yet) —
// never a filler string disconnected from the backend.
const derivePendingLabel = (
  toolEvents: Array<{ name: string; uiLabel?: string; done: boolean }>
): string => {
  const inFlight = toolEvents.filter((t) => !t.done);
  if (inFlight.length === 0) return 'Thinking';
  const t = inFlight[inFlight.length - 1]!;
  if (t.uiLabel) return t.uiLabel;
  const after = t.name.includes('.') ? t.name.slice(t.name.indexOf('.') + 1) : t.name;
  const spaced = after.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.length ? spaced[0]!.toUpperCase() + spaced.slice(1) : t.name;
};

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ open, onClose, pageContext, initialPrompt, initialThreadId }) => {
  const [thread, setThread] = useState<AiThread | null>(null);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [inspectedBundle, setInspectedBundle] = useState<{ id: string; readOnly: boolean } | null>(null);
  // 'list' = thread switcher; 'chat' = active conversation. We default to
  // 'chat' on first open so users land on a usable composer; the user opens
  // 'list' explicitly via the "Threads" button.
  const [view, setView] = useState<'list' | 'chat'>('chat');
  const createThread = useCreateAiThread();
  const updateThread = useUpdateAiThread();
  const stream = useChatStream();
  const queryClient = useQueryClient();
  const writesEnabled = useFeatureFlag('ai.agents.writes.enabled');
  const threadsQuery = useAiThreads();
  const historyQuery = useThreadMessages(thread?.id ?? null);
  // Guard so we auto-submit initialPrompt at most once per open cycle.
  const submittedInitialPromptRef = useRef<string | null>(null);
  // Track which threads we've hydrated to avoid re-loading on every render.
  const hydratedThreadIdRef = useRef<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset the initial-prompt guard whenever the sidebar closes so reopens
  // with a fresh prompt still auto-submit.
  useEffect(() => {
    if (!open) submittedInitialPromptRef.current = null;
  }, [open]);

  // When opening with a specific thread, select it as soon as thread data is available.
  const handledInitialThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) { handledInitialThreadRef.current = null; return; }
    if (!initialThreadId || handledInitialThreadRef.current === initialThreadId) return;
    const t = threadsQuery.data?.find((x) => x.id === initialThreadId);
    if (t) {
      setThread(t);
      setView('chat');
      handledInitialThreadRef.current = initialThreadId;
    }
  }, [open, initialThreadId, threadsQuery.data]);

  // When the active thread changes, hydrate `messages` from server history
  // exactly once per thread id. Streaming messages append on top of this.
  useEffect(() => {
    if (!thread) {
      setMessages([]);
      hydratedThreadIdRef.current = null;
      return;
    }
    if (hydratedThreadIdRef.current === thread.id) return;
    if (!historyQuery.data) return;
    const hydrated = historyQuery.data
      .map(toLocalMessage)
      .filter((m): m is LocalMessage => m !== null);
    setMessages(hydrated);
    hydratedThreadIdRef.current = thread.id;

    // Retroactively title threads that still have the default placeholder title.
    if (thread.title === 'New conversation' || !thread.title) {
      const firstUserMsg = historyQuery.data.find((m) => m.role === 'user');
      if (firstUserMsg) {
        updateThread.mutate({ id: thread.id, patch: { title: deriveTitle(firstUserMsg.content) } });
      }
    }
  }, [thread, historyQuery.data]);

  // Background sweep: fix any untitled threads whose messages are already cached.
  useEffect(() => {
    if (!open || !threadsQuery.data) return;
    const untitled = threadsQuery.data.filter(
      (t) => (t.title === 'New conversation' || !t.title) && t.messageCount > 0
    );
    for (const t of untitled) {
      const cached = queryClient.getQueryData<AiMessage[]>(aiQueryKeys.threadMessages(t.id));
      if (!cached) continue;
      const firstUserMsg = cached.find((m) => m.role === 'user');
      if (firstUserMsg) {
        updateThread.mutate({ id: t.id, patch: { title: deriveTitle(firstUserMsg.content) } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, threadsQuery.data]);

  const ensureThread = async (): Promise<AiThread> => {
    if (thread) return thread;
    const t = await createThread.mutateAsync({ title: 'New conversation' });
    let finalThread: AiThread = t;
    if (pageContext && !t.pinnedTo) {
      finalThread = await updateThread.mutateAsync({
        id: t.id,
        patch: {
          pinnedTo: {
            pluginId: pageContext.pluginId,
            entityType: pageContext.entityType,
            entityId: pageContext.entityId,
          },
        },
      });
    }
    setThread(finalThread);
    hydratedThreadIdRef.current = finalThread.id; // skip hydration — it's empty
    return finalThread;
  };

  const onSelectThread = (threadId: string) => {
    const t = threadsQuery.data?.find((x) => x.id === threadId);
    if (!t) return;
    setThread(t);
    setView('chat');
  };

  const onNewThread = () => {
    setThread(null);
    setMessages([]);
    hydratedThreadIdRef.current = null;
    setView('chat');
  };

  // Always reflect the latest title from the server even when local `thread` state is stale.
  const liveThread = thread ? (threadsQuery.data?.find((t) => t.id === thread.id) ?? thread) : null;

  // Resolve entity label for pinned threads so the header can show e.g. "Sales order SO-2026-00020".
  const pinnedEntityLabel = useEntityLabel({
    pluginId: liveThread?.pinnedTo?.pluginId,
    entityType: liveThread?.pinnedTo?.entityType,
    entityId: liveThread?.pinnedTo?.entityId,
  });

  const headerTitle: string | null = (() => {
    if (!liveThread) return null;
    if (liveThread.title && liveThread.title !== 'New conversation') return liveThread.title;
    if (liveThread.pinnedTo) {
      const entityTypeLabel = humanize(liveThread.pinnedTo.entityType);
      if (pinnedEntityLabel.label) return `${entityTypeLabel} ${pinnedEntityLabel.label}`;
      if (!pinnedEntityLabel.isPending) return entityTypeLabel;
    }
    return null;
  })();

  const sortedThreads = useMemo(() => {
    if (!threadsQuery.data) return [];
    return [...threadsQuery.data].sort((a, b) => {
      const at = new Date(a.lastMessageAt ?? a.updatedAt).getTime();
      const bt = new Date(b.lastMessageAt ?? b.updatedAt).getTime();
      return bt - at;
    });
  }, [threadsQuery.data]);

  const handleSubmit = async (text: string) => {
    const isNewThread = !thread;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    const t = await ensureThread();
    const { finalText, finalTraces, persisted } = await stream.send({ threadId: t.id, content: text, pageContext });
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: finalText,
        toolTrace: finalTraces.map((tr) => ({ name: tr.name, uiLabel: tr.uiLabel, done: tr.done })),
        draftRefs: [],
        messageId: persisted?.assistantMessageId,
      },
    ]);

    if (finalTraces.some((tr) => tr.name === 'ai.addNote')) {
      queryClient.invalidateQueries({ queryKey: [...aiQueryKeys.all, 'notes'] });
    }

    // Auto-title the thread from the first user message so it's never "Untitled chat".
    // Write directly into the cache so liveThread reflects the title immediately
    // without waiting for the background refetch triggered by invalidateQueries.
    if (isNewThread) {
      const updated = await updateThread.mutateAsync({ id: t.id, patch: { title: deriveTitle(text) } });
      setThread(updated);
      queryClient.setQueryData<AiThread[]>(aiQueryKeys.threads(), (old) =>
        old ? old.map((x) => (x.id === updated.id ? updated : x)) : [updated]
      );
    }

    // Bubble the active thread to the top of the list and refresh history
    // so re-opening this thread later shows the new turn from server truth.
    queryClient.invalidateQueries({ queryKey: aiQueryKeys.threads() });
    queryClient.invalidateQueries({ queryKey: aiQueryKeys.threadMessages(t.id) });
  };

  // Auto-submit the palette-supplied prompt on open. Guarded so React Strict
  // Mode double-invoke doesn't double-submit.
  useEffect(() => {
    if (!open) return;
    const prompt = initialPrompt?.trim();
    if (!prompt) return;
    if (submittedInitialPromptRef.current === prompt) return;
    submittedInitialPromptRef.current = prompt;
    void handleSubmit(prompt);
    // handleSubmit is stable within the component body and intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrompt]);

  const streamingLine: LocalMessage | null =
    stream.isStreaming && stream.currentText
      ? {
          role: 'assistant',
          content: stream.currentText,
          toolTrace: stream.toolEvents.map((t) => ({ name: t.name, uiLabel: t.uiLabel, done: t.done })),
          draftRefs: [],
        }
      : null;

  // Shown when we're streaming but nothing has been rendered yet — always
  // reflects a real backend action (current tool name) or "Thinking".
  const pendingLine: LocalMessage | null =
    stream.isStreaming && !stream.currentText
      ? {
          role: 'assistant',
          content: `_${derivePendingLabel(stream.toolEvents)}…_`,
          toolTrace: stream.toolEvents.map((t) => ({ name: t.name, uiLabel: t.uiLabel, done: t.done })),
        }
      : null;

  // After a page refresh stream.bundleIds is empty even when a draft bundle is
  // still pending server-side. Fall back to the latest still-active bundle the
  // server knows about for this thread so the DraftBundleCard re-mounts.
  const activeBundlesQuery = useAiBundlesForThread(thread?.id ?? null);
  const persistedActiveBundleId = activeBundlesQuery.data?.[0]?.id ?? null;
  const currentBundleId =
    stream.bundleIds[stream.bundleIds.length - 1] ?? persistedActiveBundleId;

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes ai-gradient-sweep {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        data-testid="chat-scrim"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 14 }}
      />
      <aside
        role="complementary"
        aria-label="Ask LognAI"
        data-testid="chat-sidebar"
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 520,
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
          zIndex: 15,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              aria-label={view === 'list' ? 'Back to chat' : 'Show conversations'}
              onClick={() => setView(view === 'list' ? 'chat' : 'list')}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0, color: 'var(--muted-foreground)', lineHeight: 1 }}
              title={view === 'list' ? 'Back to chat' : 'Conversations'}
            >
              {view === 'list' ? '←' : '☰'}
            </button>
            {view === 'list' ? (
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--foreground)' }}>
                Conversations
              </span>
            ) : (
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Ask LognAI
              </span>
            )}
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted-foreground)', lineHeight: 1, padding: 4 }}
          >
            ✕
          </button>
        </header>
        {view === 'list' ? (
          <ThreadList
            threads={sortedThreads}
            activeThreadId={thread?.id ?? null}
            isLoading={threadsQuery.isPending}
            onSelect={onSelectThread}
            onNew={onNewThread}
          />
        ) : (
          <>
            {headerTitle && (
              <div style={{ padding: '10px 16px 10px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--foreground)' }}>
                  {headerTitle}
                </span>
              </div>
            )}
            <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
              <div style={{ height: '100%', padding: '12px 14px', overflow: 'auto', fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {thread && historyQuery.isPending && messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 12 }}>Loading history…</div>
                ) : null}
                {messages.map((m, i) => (
                  <ChatMessage
                    key={m.messageId ?? i}
                    role={m.role}
                    content={m.content}
                    toolTrace={m.toolTrace}
                    draftRefs={m.draftRefs}
                    threadId={thread?.id}
                    messageId={m.messageId}
                  />
                ))}
                {streamingLine && <ChatMessage role={streamingLine.role} content={streamingLine.content} toolTrace={streamingLine.toolTrace} draftRefs={streamingLine.draftRefs} />}
                {pendingLine && <ChatMessage role={pendingLine.role} content={pendingLine.content} toolTrace={pendingLine.toolTrace} draftRefs={pendingLine.draftRefs} />}
                {stream.error && (
                  <div role="alert" style={{ color: 'var(--destructive)', fontSize: 12 }}>{stream.error}</div>
                )}
                {currentBundleId && (
                  <DraftBundleCard
                    bundleId={currentBundleId}
                    onReview={(id) => setInspectedBundle({ id, readOnly: false })}
                    onViewHistory={(id) => setInspectedBundle({ id, readOnly: true })}
                  />
                )}
                {stream.undoBanners.map((b) => (
                  <UndoBanner
                    key={b.draftId}
                    draftId={b.draftId}
                    bundleId={b.bundleId}
                    deadlineAt={b.deadlineAt}
                    label={b.label}
                    onDismiss={() => stream.dismissUndoBanner(b.draftId)}
                  />
                ))}
              </div>
              {view === 'chat' && stream.isStreaming && (
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 120,
                    pointerEvents: 'none',
                    background: 'linear-gradient(90deg, rgba(37,99,235,0.2) 0%, rgba(37,99,235,0.2) 50%, rgba(37,99,235,0.2) 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'ai-gradient-sweep 2.5s ease infinite',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 100%)',
                    maskImage: 'linear-gradient(to bottom, transparent 0%, black 100%)',
                  }}
                />
              )}
            </div>
          </>
        )}
        {view === 'chat' && !writesEnabled && <KillSwitchBanner />}
        {view === 'chat' && (
          <ChatComposer
            onSubmit={handleSubmit}
            disabled={stream.isStreaming || (createThread.isPending && !thread)}
          />
        )}
      </aside>
      <BundleReviewPanel
        bundleId={inspectedBundle?.id ?? null}
        open={Boolean(inspectedBundle)}
        onClose={() => setInspectedBundle(null)}
        readOnly={inspectedBundle?.readOnly ?? false}
      />
    </>
  );
};
